
import React, { useState, useMemo } from 'react';
import { Coins, CreditCard, Loader2, ShieldCheck, AlertTriangle } from 'lucide-react';
import PayPalFiatCheckout from './PayPalFiatCheckout';
import { PLATFORM_FEE_PERCENT } from '@/config';
import { useWallet } from '@/contexts/WalletContext';

interface PaymentGatewayProps {
  amount: number | string;
  currency: string;
  itemDescription: string;
  onSuccess: (method: 'crypto' | 'fiat') => void;
  onCancel: () => void;
  userId?: string;
  token?: string;
  marketplaceItemId?: string;
  quantity?: number;
}

const MOCK_RATES: Record<string, number> = {
  USD: 1,
  EUR: 0.92,
  INR: 83.33,
  MATIC: 0.55,
};

const PaymentGateway: React.FC<PaymentGatewayProps> = ({
  amount,
  currency,
  itemDescription,
  onSuccess,
  onCancel,
  userId,
  token,
  marketplaceItemId,
  quantity,
}) => {
  const [method, setMethod] = useState<'crypto' | 'fiat'>('fiat');
  const [isProcessing, setIsProcessing] = useState(false);
  const [fiatCurrency, setFiatCurrency] = useState('USD');
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const { isConnected, swap } = useWallet();

  const baseAmountUSD = useMemo(() => {
    const amountNum = Number(amount) || 0;
    const currencyCode = (currency.split(' ')[0] || 'USD').toUpperCase();
    const rate = MOCK_RATES[currencyCode] || 1;
    return amountNum / rate;
  }, [amount, currency]);

  const convertedAmount = baseAmountUSD * (MOCK_RATES[fiatCurrency] || 1);
  const platformFee = (convertedAmount * PLATFORM_FEE_PERCENT) / 100;
  const totalAmount = convertedAmount + platformFee;

  const handleCryptoPay = async () => {
    if (!isConnected) {
      alert('Please connect your wallet first.');
      return;
    }

    setIsProcessing(true);
    setCheckoutError(null);
    try {
      await swap('kala', 'usd', Number(amount));
      onSuccess('crypto');
    } catch (error) {
      console.error('Crypto payment failed:', error);
      setCheckoutError('Crypto payment failed. Please check your wallet and try again.');
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleFiatError = (error: string) => {
    setCheckoutError(error);
    setIsProcessing(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-kala-900 border border-kala-700 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
        <div className="p-4 border-b border-kala-800 bg-kala-800/50 flex justify-between items-center">
          <h3 className="text-white font-bold text-lg flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-green-400" /> Secure Checkout
          </h3>
        </div>

        <div className="p-6 space-y-6">
          <div className="space-y-2 bg-kala-800/30 rounded-lg p-4 border border-kala-700">
            <div className="text-sm text-kala-300 font-medium mb-2">
              {itemDescription}
            </div>
            <div className="flex justify-between items-center">
              <div className="text-sm text-kala-400">Item Cost</div>
              <div className="text-sm font-mono text-kala-300">
                {convertedAmount.toFixed(2)} {fiatCurrency}
              </div>
            </div>
            <div className="flex justify-between items-center">
              <div className="text-sm text-kala-400">
                Platform Fee ({PLATFORM_FEE_PERCENT}%)
              </div>
              <div className="text-sm font-mono text-kala-400">
                {platformFee.toFixed(2)} {fiatCurrency}
              </div>
            </div>
            <div className="border-t border-kala-700 my-2"></div>
            <div className="flex justify-between items-center">
              <div className="text-base text-white font-bold">Total</div>
              <div className="text-lg font-mono font-bold text-white">
                {totalAmount.toFixed(2)} {fiatCurrency}
              </div>
            </div>
          </div>

          {checkoutError && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-300 p-3 rounded-lg text-sm flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0"/>
              <div>
                <p className="font-bold">Payment Error</p>
                <p className="text-xs mt-1">{checkoutError}</p>
              </div>
              <button onClick={() => setCheckoutError(null)} className="ml-auto text-red-300 hover:text-white">&#x2715;</button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
             <button
                onClick={() => { setMethod('fiat'); setCheckoutError(null); }}
                className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition-all relative overflow-hidden ${
                  method === 'fiat'
                    ? 'bg-green-500/20 border-green-500 text-white'
                    : 'bg-kala-800 border-kala-700 text-kala-400 hover:bg-kala-700'
                }`}>
                <CreditCard className="w-8 h-8 mb-1" />
                <span className="text-sm font-bold">Card / Fiat</span>
            </button>
             <button
                onClick={() => { setMethod('crypto'); setCheckoutError(null); }}
                className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition-all relative overflow-hidden ${
                  method === 'crypto'
                    ? 'bg-purple-500/20 border-purple-500 text-white'
                    : 'bg-kala-800 border-kala-700 text-kala-400 hover:bg-kala-700'
                }`}>
                <Coins className="w-8 h-8 mb-1" />
                <span className="text-sm font-bold">Crypto Wallet</span>
            </button>
          </div>

          {method === 'fiat' && (
             <div className="space-y-4 animate-in fade-in duration-300">
                <select
                    value={fiatCurrency}
                    onChange={(e) => setFiatCurrency(e.target.value)}
                    className="w-full bg-kala-800 border border-kala-700 rounded-lg px-3 py-2 text-white focus:ring-1 focus:ring-green-500 outline-none">
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="INR">INR - Indian Rupee</option>
                </select>
                {totalAmount > 0 ? (
                  <PayPalFiatCheckout
                      amount={totalAmount}
                      currency={fiatCurrency}
                      description={itemDescription}
                      userId={userId}
                      token={token}
                      marketplaceItemId={marketplaceItemId}
                      quantity={quantity}
                      onSuccess={() => { setCheckoutError(null); onSuccess('fiat'); }}
                      onError={handleFiatError}
                  />
                ) : (
                  <div className="text-center text-kala-400 p-4 bg-kala-900/40 rounded-lg">
                    <p>Please specify an amount to pay.</p>
                  </div>
                )}
            </div>
          )}

          {method === 'crypto' && (
             <div className="text-center p-4 bg-purple-900/20 rounded-lg border border-purple-500/30 animate-in fade-in duration-300">
                <p className="text-purple-200 text-sm mb-3">Crypto payments are handled via your connected wallet.</p>
                 <button
                    onClick={handleCryptoPay}
                    disabled={isProcessing || !isConnected}
                    className="w-full py-3 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-500 flex items-center justify-center gap-2 transition-all disabled:opacity-50">
                     {isProcessing ? <Loader2 className="w-5 h-5 animate-spin" /> : (isConnected ? 'Proceed with Crypto' : 'Connect Wallet to Pay')}
                </button>
             </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              onClick={onCancel}
              disabled={isProcessing}
              className="w-full py-3 rounded-xl border border-kala-600 text-kala-400 font-bold hover:text-white hover:bg-kala-800 transition-colors">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentGateway;
